/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect } from 'react';
import ReactModal from 'react-modal';

import { AdminUserRoles_ScopeLevel as AdminUserRolesScopeLevel } from '__generated__/graphql-types';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { branch, compose, getContext } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Retracked from 'js/app/retracked';
import type { ActionContext } from 'js/lib/ActionContext';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import type { TrackingContextData } from 'js/lib/retracked';

import { Typography2 } from '@coursera/cds-core';

import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import withCustomLabelsByUserAndCourse from 'bundles/custom-labels/hoc/withCustomLabelsByUserAndCourse';
import EditRoleAssignmentDialog from 'bundles/staff-management/src/dialogs/EditRoleAssignmentDialog';
import InviteStaffDialog from 'bundles/staff-management/src/dialogs/InviteStaffDialog';

import _t from 'i18n!nls/staff-management';

import 'css!../__styles__/StaffManagement';

import ActivityModal from '../ActivityModal';
import AddModal from '../AddModal';
import DelModal from '../DelModal';
import EditModal from '../EditModal';
import InviteModal from '../InviteModal';
import PaintedDoor from '../PaintedDoor';
import StaffManagementNotification from '../StaffManagementNotification';
import WithUserRoleModal from '../StaffPermissions/components/WithUserRoleModal';
import { trackStaffManagementPageViewEvent } from '../StaffPermissions/utils/eventingUtils';
import Notifications from '../common/notifications/Notifications';
import type { ObjectId, StaffManagementStoreV2 as StaffManagementStoreClass } from '../store';
import { actions } from '../store';
import { StaffMembershipType } from '../store/types/staffMemberships';
import withErrorNotification from '../withErrorNotification';
import withFluxibleContextAsProps from '../withFluxibleContextAsProps';
import withLoadingIndicator from '../withLoadingIndicator';
import NoStaff from './NoStaff';
import StaffTable from './StaffTable';

const styles = {
  oldHeaderMessage: css`
    margin-bottom: 30px;
  `,
  headerMessage: css`
    margin-top: var(--cds-spacing-150);
    margin-bottom: var(--cds-spacing-300);
  `,
};

type PropsFromGetContext = {
  _eventData: TrackingContextData;
};

export type Props = {
  /** If true, component is creating memberships. */
  creating?: boolean;
  /** If true, a batch of memberships are being deleted. */
  deleting?: boolean;
  /** Id of entity of `type` we will be managing memberships of. */
  fromId?: ObjectId;
  /** Called during componentDidMount. */
  load?(): void;
  /** How many users are already staff members? */
  numUsers?: number;
  /** Associated partner id. Used to retrieve membership roles. */
  partnerId: ObjectId;
  /** Provides nuances for particular type of staff management. */
  type: keyof typeof StaffMembershipType;
  /** Called during componentWillUnmount. */
  unload?(): void;
  /** If true, component is updating memberships. */
  updating?: boolean;
  /** Name of the degree */
  degreeName?: string;
  /** Name of the partner institution */
  partnerName?: string;
  /** Use the table rendering method developed during [LA-256](https://coursera.atlassian.net/browse/LA-256) */
  variant?: 'CDSLikeTable' | 'CDSTable';
} & PropsFromGetContext;

export type PropsFromCaller = Pick<Props, 'fromId' | 'partnerId' | 'type' | 'partnerName' | 'degreeName' | 'variant'>;

export const StaffManagement = ({
  creating,
  deleting,
  numUsers,
  type,
  updating,
  degreeName,
  partnerName,
  fromId,
  partnerId,
  _eventData,
  variant,
}: Props) => {
  const scopeData = {
    scopeLevel: AdminUserRolesScopeLevel.Degree,
    scopeId: `${fromId ?? ''}`,
    scopeName: degreeName ?? '',
  };

  useEffect(() => {
    // We use `react-modal` in conjunction with `@coursera-ui` components. Since it may not be set for us,
    // we make a reasonable guess for providing ReactModal the information it needs to behave correctly
    // for screen readers.
    try {
      ReactModal.setAppElement('#rendered-content');
    } catch {
      // Fall back to our own element which we know will be in the page.
      ReactModal.setAppElement('.rc-StaffManagement');
    }
    // ideally, we should not need to check the type here
    // since this component is meant to be shared by the staff management pages across multiple apps
    // however, this component is not actually shared and only used in the program-level staff management page
    // thus we need to check if the type (i.e. scopeLevel) is actually "program"
    // and hardcode the value of the input to trackStaffManagementPageViewEvent()
    if (_eventData && type === StaffMembershipType.program && !!fromId && !!degreeName) {
      trackStaffManagementPageViewEvent(_eventData, scopeData);
    }
    // this side effect is only meant to be executed once and the depedency array is left to be empty intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const e2eName = 'staff-management';

  let headerMessage = '';
  let learnMoreLinkArticleId = '';
  let title = '';
  let searchPlaceholderText = '';

  if (!numUsers && variant !== 'CDSTable') {
    return (
      <div className="rc-StaffManagement" data-e2e={e2eName}>
        <NoStaff />
        <AddModal partnerName={partnerName} degreeName={degreeName} />
      </div>
    );
  } else {
    if (type === 'partner') {
      headerMessage = _t(
        'You can manage staff for your institution. Add or delete staff, edit role assignments. {learnMoreLink}'
      );
      learnMoreLinkArticleId = '360044740752';
      title = _t('Institution Staff');
      searchPlaceholderText = _t('Search by name, email, or role...');
    } else if (type === 'program') {
      learnMoreLinkArticleId = '360055987132';
      searchPlaceholderText = _t('Search by name, session or others');
    }

    const redesignVariant = variant === 'CDSTable';

    return (
      <div className="rc-StaffManagement" data-e2e={e2eName}>
        <div>
          <Notifications msBeforeNotificationClose={5000}>
            <Typography2 component="h1" variant={redesignVariant ? 'titleXSmall' : 'titleMedium'}>
              {title}
            </Typography2>
            <div>
              <Typography2
                component="p"
                color={redesignVariant ? 'supportText' : undefined}
                css={redesignVariant ? styles.headerMessage : styles.oldHeaderMessage}
              >
                <FormattedMessage
                  message={headerMessage}
                  learnMoreLink={<PartnerHelpLink articleId={learnMoreLinkArticleId} />}
                />
              </Typography2>
              <WithUserRoleModal partnerId={partnerId} eventData={_eventData} ingressPointScopeData={scopeData}>
                <StaffTable variant={variant} searchPlaceholderText={searchPlaceholderText} />
              </WithUserRoleModal>
            </div>

            {redesignVariant ? (
              <InviteStaffDialog />
            ) : (
              // Some states are displayed as modals. It is assumed the expressed state will be
              // managed to avoid conflicting modals, like Deleting + Adding.
              <AddModal partnerName={partnerName} degreeName={degreeName} />
            )}
            <DelModal />
            {redesignVariant ? <EditRoleAssignmentDialog /> : <EditModal />}
            <InviteModal />
            <StaffManagementNotification />
            <PaintedDoor />
            {deleting ? <ActivityModal message={_t('Deleting roles...')} /> : null}
            {creating ? <ActivityModal message={_t('Creating roles...')} /> : null}
            {updating ? <ActivityModal message={_t('Updating roles...')} /> : null}
          </Notifications>
        </div>
      </div>
    );
  }
};

export default compose<Props, PropsFromCaller>(
  branch(({ type }: Props) => type === 'course', withCustomLabelsByUserAndCourse),
  withFluxibleContextAsProps(),
  connectToStores<Props, Props & ActionContext>(
    ['StaffManagementStoreV2'],
    (
      { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
      { executeAction, fromId, partnerId, type }
    ) => {
      const state = StaffManagementStoreV2.get();
      return {
        load: () => {
          return executeAction(actions.page.load, { fromId: fromId as ObjectId, type, partnerId });
        },
        unload: () => {
          return executeAction(actions.page.unload);
        },
        creating: state.data?.creating,
        deleting: state.data?.deleting,
        error: state.data?.error,
        // Initializing and loading has the same effect on the component.
        loading: !!(state.data?.loading || state?.data?.initializing),
        numUsers: _.keys(state.data?.users).length,
        updating: state.data?.updating,
      };
    }
  ),
  withErrorNotification(),
  withLoadingIndicator(),
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        page: 'staff_management_program',
      },
    };
  }),
  getContext({ _eventData: PropTypes.object })
)(StaffManagement);
